import React from 'react';
import { Box, Button } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';

/**
 * ProgressButton — a button that doubles as a determinate progress bar.
 *
 * @description
 *   Renders a MUI Button. While `running`, an absolutely-positioned fill
 *   (theme `primary` overlay) grows across the button width driven by
 *   `progress` (0..1), behind the label. When `running` with a null
 *   `progress` it shows an indeterminate shimmer instead. Solves the
 *   "kick a long sequence and watch it fill" pattern without a separate
 *   progress bar element next to the button.
 *
 *   🔴 `aria-busy` traegt der Knopf SELBST, und das ist nicht Kosmetik: MUI
 *   setzt an einem Knopf im Ladezustand nur natives `disabled` (gemessen
 *   2026-10-10 an `@mui/material` 7.3.5) — die Zusage „hier laeuft etwas"
 *   fehlte damit im Baum der Hilfstechnik. `RefreshButton` traegt sie seit
 *   jeher, und diese Zeile zieht die Bauform nach.
 *
 *   🔴 Dazu eine Live-Region mit dem laufenden Text. Bei einem Fortschrittswert
 *   AENDERT sich die Beschriftung („Syncing 42 / 150" → „Syncing 43 / 150"), und
 *   ohne `aria-live` bliebe jede dieser Aenderungen ungehoert — der sichtbare
 *   Text steht im Knopf, nicht in einer Region. Sie steht IMMER im DOM (leer,
 *   wenn nicht laufend): eine Region, die erst zusammen mit ihrem Text
 *   erscheint, wird von Sprachausgaben nicht zuverlaessig angesagt.
 *
 *   🔴 Die Region steht NEBEN dem Knopf, nicht in ihm (R16 B1 / R15 W1,
 *   2026-10-10). ARIA 1.2 fuehrt `button` unter den Rollen mit **`Children
 *   Presentational: True`** — ein Nachfahre wird aus dem Baum der Hilfstechnik
 *   entfernt, samt Live-Region-Semantik. Die Region als Kind waere damit genau
 *   die Zusage, die sie zu sein scheint, nicht. Hausform ist `CopyButton.jsx`
 *   (`:264`, Region als Geschwister, mit eigener Begruendung ab `:222`); dieser
 *   Baustein zieht die zweite Haelfte der Form nach. Weil der Regionstext damit
 *   NICHT mehr in die Namensberechnung des Knopfes faellt, entfaellt auch das
 *   fruehere `aria-label`: der zugaengliche Name kommt wieder aus dem Inhalt.
 *
 *   ⚠️ **Der Ort hat einen Preis, den die Aufrufer kennen muessen:** in einem
 *   `ButtonGroup` ist ein zweites Kind kein Knopf mehr — MUI klont seine Kinder
 *   und vergaebe die Gruppenklassen an ein Fragment, das sie nicht traegt (die
 *   linke Haelfte verloere ihre Randrundung). `SplitButton` setzt deshalb
 *   `statusRegion={false}` und rendert `ProgressStatus` selbst AUSSERHALB der
 *   Gruppe. Ein `Stack` traegt die Geschwisterform dagegen ohne Zutun: MUI
 *   vergibt seine Abstaende ueber den Kindselektor `:not(style)`, nicht ueber
 *   einen Klon.
 *
 *   🔴 Der `ref` wird DURCHGEREICHT (K-F1, 2026-10-10). Er ist die Voraussetzung
 *   dafuer, dass ein Aufrufer den Fokusverlust beim Laden auffangen kann
 *   (`useFokusNachLaden`): MUI setzt am gesperrten Knopf natives `disabled`, das
 *   Element gibt den Fokus an `body` ab, und ohne Ref kommt niemand an das
 *   Element heran, um ihn zurueckzuholen. Ein Baustein, der einen Knopf rendert,
 *   traegt seinen Ref weiter — wie jeder MUI-Knopf.
 *
 * @useWhen
 *   - A single action triggers a long determinate job ("Syncing 42 / 150")
 *   - You want the button itself to communicate progress, in place
 *   - Multi-step sequences where the button label = the current step
 *
 * @dontUseFor
 *   - Plain instant actions — use a normal MUI Button
 *   - Background jobs whose progress belongs in the header sync panel only
 *   - Toggles / segmented controls (use ToggleButtonGroup)
 *
 * @related
 *   - Button (MUI) — what to use when there's no progress to show
 *   - SidebarAndHeaderBar job panel — shows the SAME jobs as distinct entries
 *
 * @reuseHints
 *   - Any "Run + watch fill" CTA: bulk re-embed, bulk price refresh, exports
 *   - PO bulk-send / bulk-receive progress buttons
 *
 * @props
 *   - running — when true, shows the fill + disables the button + sets `aria-busy`
 *   - progress — 0..1 fraction for the fill, or null for indeterminate
 *   - label — text shown (e.g. "Syncing 42 / 150"); falls back to children
 *   - onClick — fired when not running
 *   - disabled — disable even when not running
 *   - variant — MUI Button variant (default "contained")
 *   - children — fallback label when `label` is not set
 *   - ref — auf den `button` durchgereicht (`forwardRef`), fuer `useFokusNachLaden`
 *   - statusRegion — Vorgabe `true`: die Live-Region wird als Geschwister
 *     gerendert. `false` nur, wenn der Aufrufer sie selbst setzt, weil er den
 *     Knopf in eine `ButtonGroup` haengt (`SplitButton`) — dort darf kein
 *     zweites Kind stehen.
 */

/**
 * ProgressStatus — die Live-Region des Fortschrittsknopfes, als GESCHWISTER.
 *
 * @description
 *   Eine `role="status"`-Region, immer im DOM, visuell versteckt, mit dem
 *   laufenden Text (leer im Ruhezustand). Sie existiert als eigener Export,
 *   weil `SplitButton` sie ausserhalb seiner `ButtonGroup` setzen muss: ein
 *   Fragment als Gruppenkind verloere die Gruppenklassen. Wer sie selbst
 *   setzt, reicht `statusRegion={false}` an `ProgressButton`.
 *
 *   Nur echte Zeichenketten gehen hinein — ein Knoten als `label` hat keinen
 *   Text, den eine Sprachausgabe vorlesen koennte, und ein leerer Text waere
 *   ehrlicher als ein vorgelesenes `[object Object]`.
 *
 *   🔴 **Zweiter Verbraucher seit dem 2026-10-16: die sammelnde Statusregion
 *   einer LISTE** (`SecondLifeBestand.jsx:1591`). Dort laeuft nicht EIN Knopf,
 *   sondern eine beliebige Zahl von Zeilen gleichzeitig (das Auswahlmodul
 *   filtert, es deckelt nicht) — und angesagt werden sollen sie alle. N
 *   Live-Regionen waeren N Ansagen; gebraucht wird EINE, die ihren Inhalt
 *   wechselt. Genau die zwei Mechanismen dafuer traegt dieser Bauteil schon:
 *   `role="status"` + `aria-live="polite"` UND „immer im DOM, auch leer".
 *   Deshalb ist er **erweitert** und nicht lokal nachgebaut — eine zweite
 *   Fassung derselben Bauform liefe auseinander, und der Grill hatte ihn fuer
 *   genau diesen Fall empfohlen (Regel-15-Auflage B2, 2026-10-16).
 *
 *   Vorbild im Repo ist die **zweite Hausform** `PODetail.jsx:3946-3951`
 *   („O21 — die EINZIGE Live-Region der Zeilenliste … immer eingehaengt,
 *   wechselt nur den Inhalt"). Dieselbe Bauart, ein anderer Aufrufer — kein
 *   Widerspruch, sondern dieselbe Regel.
 *
 * @useWhen
 *   - `statusRegion={false}` am `ProgressButton` (der `ButtonGroup`-Fall)
 *   - Eine sammelnde Statusregion fuer eine LISTE, ausserhalb eines
 *     Fortschrittsknopfes — mehrere gleichzeitig laufende Zeilen in EINER
 *     Ansage (`SecondLifeBestand.jsx:1591`; Vorbild `PODetail.jsx:3946`)
 *
 * @dontUseFor
 *   - Eine Statusmeldung, die der jeweilige Baustein schon selbst ansagt —
 *     dann dessen Region nehmen (`Alert` mit `role="status"`), nicht eine
 *     zweite daneben
 *   - Mehrere Regionen fuer DIESELBE Liste — es ist EINE sammelnde, nicht eine
 *     je Zeile
 * @related ProgressButton (setzt sie selbst) · SplitButton · CopyButton (Bauform)
 *   · SecondLifeBestand (die sammelnde Listenregion, 2026-10-16) · PODetail
 *   (dieselbe Bauart fuer die MOQ-Zeilenliste)
 * @props
 *   - text — der anzusagende Text; alles ausser einer Zeichenkette wird leer
 */
export function ProgressStatus({ text }) {
  return (
    <Box component="span" role="status" aria-live="polite" sx={visuallyHidden}>
      {typeof text === 'string' ? text : ''}
    </Box>
  );
}

const ProgressButton = React.forwardRef(function ProgressButton({
  running = false,
  progress = null,
  label,
  onClick,
  disabled = false,
  variant = 'contained',
  children,
  statusRegion = true,
  ...buttonProps
}, ref) {
  const hasFraction = running && typeof progress === 'number';
  const pct = hasFraction ? Math.max(0, Math.min(1, progress)) * 100 : 0;
  const content = label ?? children;

  const knopf = (
    <Button
      ref={ref}
      size="small"
      variant={variant}
      color="primary"
      onClick={running ? undefined : onClick}
      disabled={disabled || running}
      // Keep the determinate fill visible even while disabled.
      sx={{
        position: 'relative',
        overflow: 'hidden',
        ...(running && {
          '&.Mui-disabled': { color: variant === 'contained' ? 'primary.contrastText' : 'primary.main' },
        }),
      }}
      {...buttonProps}
      // 🔴 NACH dem Spread: den Ladezustand besitzt der Baustein, nicht der
      // Aufrufer. Vor dem Spread koennte ihn ein `aria-busy` aus `buttonProps`
      // ueberschreiben, und dann behauptete der Knopf Ruhe, waehrend er laeuft.
      aria-busy={running || undefined}
    >
      {running && hasFraction && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: `${pct}%`,
            bgcolor: (theme) =>
              variant === 'contained' ? alpha(theme.palette.common.white, 0.22) : 'primary.main',
            opacity: variant === 'contained' ? 1 : 0.16,
            transition: 'width 200ms ease',
            pointerEvents: 'none',
          }}
        />
      )}
      {running && !hasFraction && (
        <Box
          aria-hidden
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            bottom: 0,
            width: '40%',
            bgcolor: (theme) =>
              variant === 'contained' ? alpha(theme.palette.common.white, 0.22) : 'primary.main',
            opacity: variant === 'contained' ? 1 : 0.16,
            pointerEvents: 'none',
            animation: 'progressButtonShimmer 1.2s ease-in-out infinite',
            '@keyframes progressButtonShimmer': {
              '0%': { transform: 'translateX(-100%)' },
              '100%': { transform: 'translateX(350%)' },
            },
          }}
        />
      )}
      <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
        {content}
      </Box>
    </Button>
  );

  // 🔴 Die Region als GESCHWISTER, nicht als Kind (Begruendung im Kopf). Im
  // `ButtonGroup`-Fall setzt der Aufrufer sie selbst — dort darf kein zweites
  // Kind stehen.
  if (!statusRegion) return knopf;
  return (
    <>
      {knopf}
      <ProgressStatus text={running ? content : ''} />
    </>
  );
});

export default ProgressButton;
